<template>
  <UModal v-model:open="isOpen" :ui="{ content: 'max-w-3xl' }">
    <template #content>
      <UCard v-if="protocol">
        <template #header>
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <UIcon name="i-lucide-shield-check" class="w-5 h-5 text-primary" />
              <h3 class="text-base font-bold">{{ title }}</h3>
              <UBadge
                v-if="isGermanFormal"
                color="info"
                variant="subtle"
                size="xs"
              >
                Formelle Inlandsprüfung
              </UBadge>
              <UBadge
                v-else
                color="primary"
                variant="subtle"
                size="xs"
              >
                Qualifizierte BZSt-Prüfung
              </UBadge>
            </div>
            <UButton color="neutral" variant="ghost" icon="i-lucide-x" @click="isOpen = false" />
          </div>
        </template>

        <div class="space-y-5 text-sm max-h-[75vh] overflow-y-auto pr-1">
          <!-- 1. Metadata Grid -->
          <div
            class="grid grid-cols-2 sm:grid-cols-5 gap-3 p-3 bg-gray-50/70 dark:bg-gray-800/40 rounded-lg border border-gray-100 dark:border-gray-800 text-xs"
          >
            <div>
              <span class="text-gray-400 block">Validierungs-ID</span>
              <span class="font-mono font-bold text-gray-800 dark:text-gray-200 truncate block">{{
                protocol.validationId
              }}</span>
            </div>
            <div>
              <span class="text-gray-400 block">Zeitpunkt</span>
              <span class="font-medium text-gray-800 dark:text-gray-200">{{
                formatTimestamp(protocol.timestamp)
              }}</span>
            </div>
            <div>
              <span class="text-gray-400 block">Status</span>
              <UBadge
                :color="statusBadgeColor"
                size="xs"
                variant="soft"
                class="font-semibold mt-0.5"
              >
                {{ effectiveStatusDisplay }}
              </UBadge>
            </div>
            <div>
              <span class="text-gray-400 block">Prüfungsart</span>
              <span class="font-medium text-gray-800 dark:text-gray-200">
                {{ isGermanFormal ? 'Formell (Regex)' : 'Qualifiziert (BZSt)' }}
              </span>
            </div>
            <div>
              <span class="text-gray-400 block">Auslöser</span>
              <span class="font-medium text-gray-800 dark:text-gray-200">{{
                protocol.trigger
              }}</span>
            </div>
          </div>

          <!-- 2. Official BZSt Status & Confirmation (Nur bei ausländischen EU-Prüfungen) -->
          <div
            v-if="!isGermanFormal"
            class="p-3.5 bg-gray-50 dark:bg-gray-800/60 rounded-lg border border-gray-200 dark:border-gray-800 space-y-2"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div class="flex items-center gap-2">
                <UIcon name="i-lucide-building" class="w-4 h-4 text-primary" />
                <span
                  class="font-semibold text-xs uppercase tracking-wider text-gray-600 dark:text-gray-300"
                >
                  Amtliche BZSt-Bestätigung (Schnittstelle:
                  {{ protocol.interface || 'BZSt_eVatR_REST_v1' }})
                </span>
              </div>
              <UBadge
                v-if="officialStatusCode"
                color="primary"
                variant="subtle"
                size="xs"
                class="font-mono font-bold"
              >
                {{ officialStatusCode }}
              </UBadge>
            </div>

            <div
              v-if="protocol.result?.officialRequestId || protocol.rawResponse?.id"
              class="text-xs"
            >
              <span class="text-gray-400">Amtliche BZSt Request-ID: </span>
              <span class="font-mono font-bold text-primary">{{
                protocol.result?.officialRequestId || protocol.rawResponse?.id
              }}</span>
            </div>

            <p class="text-xs text-gray-700 dark:text-gray-300 font-medium">
              {{ officialStatusMessage }}
            </p>

            <div
              v-if="
                protocol.result?.validFrom ||
                protocol.result?.validTill ||
                protocol.rawResponse?.gueltigAb ||
                protocol.rawResponse?.gueltigBis
              "
              class="text-xs text-gray-500 flex gap-4 pt-1 border-t border-gray-200 dark:border-gray-700"
            >
              <span v-if="protocol.result?.validFrom || protocol.rawResponse?.gueltigAb">
                Gültig ab: {{ protocol.result?.validFrom || protocol.rawResponse?.gueltigAb }}
              </span>
              <span v-if="protocol.result?.validTill || protocol.rawResponse?.gueltigBis">
                Gültig bis: {{ protocol.result?.validTill || protocol.rawResponse?.gueltigBis }}
              </span>
            </div>
          </div>

          <!-- 3a. Geprüfte Daten & Details (Formelle Inlandsprüfung: Übersichtlich ohne Doppelungen) -->
          <div v-if="isGermanFormal" class="space-y-3">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <UIcon name="i-lucide-file-check" class="w-4 h-4 text-primary" />
                <h4
                  class="font-semibold text-xs uppercase tracking-wider text-gray-700 dark:text-gray-300"
                >
                  Geprüfte Daten (Formelle Prüfung)
                </h4>
              </div>
              <span class="text-[11px] text-gray-400">Inländische Syntaxprüfung</span>
            </div>

            <div
              class="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden divide-y divide-gray-200 dark:divide-gray-800 bg-white dark:bg-gray-900"
            >
              <!-- Geprüfte USt-IdNr. -->
              <div class="grid grid-cols-12 px-3.5 py-2.5 items-center text-xs">
                <div class="col-span-4 sm:col-span-3 font-medium text-gray-600 dark:text-gray-400">
                  USt-IdNr.
                </div>
                <div class="col-span-5 sm:col-span-5 font-mono font-bold text-gray-900 dark:text-white">
                  {{
                    protocol.submittedData?.requestedVatId ||
                    protocol.rawResponse?.angefragteUstid ||
                    'Keine hinterlegt'
                  }}
                </div>
                <div class="col-span-3 sm:col-span-4 flex items-center gap-1.5 justify-end sm:justify-start">
                  <UBadge
                    :color="protocol.result?.valid ? 'success' : 'error'"
                    variant="subtle"
                    size="xs"
                    class="font-semibold"
                  >
                    {{ protocol.result?.valid ? 'Gültiges DE-Format' : 'Ungültig' }}
                  </UBadge>
                </div>
              </div>

              <!-- Geprüftes Regex-Muster -->
              <div class="grid grid-cols-12 px-3.5 py-2.5 items-center text-xs">
                <div class="col-span-4 sm:col-span-3 font-medium text-gray-600 dark:text-gray-400">
                  Prüfmuster
                </div>
                <div class="col-span-8 sm:col-span-9 font-mono text-gray-800 dark:text-gray-200">
                  ^DE\d{9}$ <span class="text-gray-400 text-[11px] font-sans">(Pattern: (DE)[0-9]{9})</span>
                </div>
              </div>

              <!-- Rechnungsanschrift Stammdaten (informatorisch) -->
              <div
                v-if="protocol.submittedData?.companyName || protocol.submittedData?.street || protocol.submittedData?.city"
                class="grid grid-cols-12 px-3.5 py-2.5 items-start text-xs bg-gray-50/40 dark:bg-gray-800/20"
              >
                <div class="col-span-4 sm:col-span-3 font-medium text-gray-600 dark:text-gray-400 pt-0.5">
                  Anschrift (Stammdaten)
                </div>
                <div class="col-span-8 sm:col-span-9 text-gray-700 dark:text-gray-300 space-y-0.5">
                  <p v-if="protocol.submittedData?.companyName" class="font-medium">
                    {{ protocol.submittedData.companyName }}
                  </p>
                  <p v-if="protocol.submittedData?.street">
                    {{ protocol.submittedData.street }}
                  </p>
                  <p v-if="protocol.submittedData?.zip || protocol.submittedData?.city">
                    {{ protocol.submittedData.zip }} {{ protocol.submittedData.city }}
                  </p>
                  <p class="text-gray-400 text-[11px]">
                    Land: {{ protocol.submittedData?.country || 'DE' }} (Inland)
                  </p>
                </div>
              </div>
            </div>

            <!-- Kompakter Hinweis: Keine BZSt-Abfrage -->
            <div
              class="flex items-start gap-2.5 text-xs text-blue-900 dark:text-blue-200 bg-blue-50/60 dark:bg-blue-950/30 p-3 rounded-lg border border-blue-200/60 dark:border-blue-900/40"
            >
              <UIcon
                name="i-lucide-info"
                class="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5"
              />
              <div class="space-y-0.5">
                <p class="font-semibold">
                  Keine Abfrage beim Bundeszentralamt für Steuern (BZSt)
                </p>
                <p class="text-gray-600 dark:text-gray-400">
                  Für deutsche USt-IdNrn. erfolgt eine formelle Syntaxprüfung anhand des regulären Ausdrucks
                  <code class="px-1 py-0.5 bg-gray-100 dark:bg-gray-800 rounded font-mono text-[11px] font-semibold text-gray-800 dark:text-gray-200">^DE\d{9}$</code>
                  (Muster: (DE)[0-9]{9}). Inländische Nummern werden nicht über die eVatR-Schnittstelle des BZSt abgefragt.
                </p>
              </div>
            </div>
          </div>

          <!-- 3b. Actual Data Checked & Qualified Match Results (Ausländische EU-Prüfungen) -->
          <div v-else>
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <UIcon name="i-lucide-file-check" class="w-4 h-4 text-primary" />
                <h4
                  class="font-semibold text-xs uppercase tracking-wider text-gray-700 dark:text-gray-300"
                >
                  Geprüfte Daten & Qualifizierte Prüfergebnisse
                </h4>
              </div>
              <span class="text-[11px] text-gray-400">Übermittelte Werte vs. BZSt-Rückmeldung</span>
            </div>

            <div
              class="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden divide-y divide-gray-200 dark:divide-gray-800"
            >
              <!-- Header -->
              <div
                class="grid grid-cols-12 bg-gray-50 dark:bg-gray-800/80 px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wider"
              >
                <div class="col-span-3">Merkmal</div>
                <div class="col-span-4">Geprüfter Wert (Übermittelt)</div>
                <div class="col-span-5">BZSt-Ergebnis (Code & Bedeutung)</div>
              </div>

              <!-- Geprüfte USt-IdNr. -->
              <div
                class="grid grid-cols-12 px-3 py-2.5 items-center hover:bg-gray-50/50 dark:hover:bg-gray-800/30 text-xs"
              >
                <div class="col-span-3 font-medium text-gray-600 dark:text-gray-400">
                  USt-IdNr. (Angefragt)
                </div>
                <div class="col-span-4 font-mono font-bold text-gray-900 dark:text-white">
                  {{
                    protocol.submittedData?.requestedVatId ||
                    protocol.rawResponse?.angefragteUstid ||
                    '-'
                  }}
                </div>
                <div class="col-span-5 flex items-center gap-1.5 flex-wrap">
                  <UBadge
                    v-if="officialStatusCode"
                    color="primary"
                    variant="subtle"
                    size="xs"
                    class="font-mono"
                  >
                    {{ officialStatusCode }}
                  </UBadge>
                  <span class="font-medium text-gray-700 dark:text-gray-300">
                    {{ protocol.result?.valid ? 'Gültig' : 'Nicht bestätigt' }}
                  </span>
                </div>
              </div>

              <!-- Firmenname -->
              <div
                class="grid grid-cols-12 px-3 py-2.5 items-center hover:bg-gray-50/50 dark:hover:bg-gray-800/30 text-xs"
              >
                <div class="col-span-3 font-medium text-gray-600 dark:text-gray-400">
                  Firmenname
                </div>
                <div
                  class="col-span-4 font-semibold text-gray-900 dark:text-white truncate"
                  :title="protocol.submittedData?.companyName"
                >
                  {{
                    protocol.submittedData?.companyName || protocol.rawResponse?.firmenname || '-'
                  }}
                </div>
                <div class="col-span-5 flex items-center gap-1.5 flex-wrap">
                  <template v-if="companyMatchInfo.code !== '-'">
                    <UIcon
                      :name="companyMatchInfo.icon"
                      :class="getMatchColorClass(companyMatchInfo.color)"
                      class="w-4 h-4 shrink-0"
                    />
                    <UBadge
                      :color="companyMatchInfo.color"
                      variant="subtle"
                      size="xs"
                      class="font-mono font-bold"
                    >
                      Code {{ companyMatchInfo.code }}
                    </UBadge>
                    <span class="font-medium text-gray-800 dark:text-gray-200">
                      {{ companyMatchInfo.meaning }}
                    </span>
                  </template>
                  <span v-else class="text-gray-400 italic">Nicht geprüft</span>
                </div>
              </div>

              <!-- Straße -->
              <div
                class="grid grid-cols-12 px-3 py-2.5 items-center hover:bg-gray-50/50 dark:hover:bg-gray-800/30 text-xs"
              >
                <div class="col-span-3 font-medium text-gray-600 dark:text-gray-400">Straße</div>
                <div
                  class="col-span-4 text-gray-900 dark:text-white truncate"
                  :title="protocol.submittedData?.street"
                >
                  {{ protocol.submittedData?.street || protocol.rawResponse?.strasse || '-' }}
                </div>
                <div class="col-span-5 flex items-center gap-1.5 flex-wrap">
                  <template v-if="streetMatchInfo.code !== '-'">
                    <UIcon
                      :name="streetMatchInfo.icon"
                      :class="getMatchColorClass(streetMatchInfo.color)"
                      class="w-4 h-4 shrink-0"
                    />
                    <UBadge
                      :color="streetMatchInfo.color"
                      variant="subtle"
                      size="xs"
                      class="font-mono font-bold"
                    >
                      Code {{ streetMatchInfo.code }}
                    </UBadge>
                    <span class="font-medium text-gray-800 dark:text-gray-200">
                      {{ streetMatchInfo.meaning }}
                    </span>
                  </template>
                  <span v-else class="text-gray-400 italic">Nicht geprüft</span>
                </div>
              </div>

              <!-- PLZ -->
              <div
                class="grid grid-cols-12 px-3 py-2.5 items-center hover:bg-gray-50/50 dark:hover:bg-gray-800/30 text-xs"
              >
                <div class="col-span-3 font-medium text-gray-600 dark:text-gray-400">
                  Postleitzahl
                </div>
                <div class="col-span-4 font-mono text-gray-900 dark:text-white">
                  {{ protocol.submittedData?.zip || protocol.rawResponse?.plz || '-' }}
                </div>
                <div class="col-span-5 flex items-center gap-1.5 flex-wrap">
                  <template v-if="zipMatchInfo.code !== '-'">
                    <UIcon
                      :name="zipMatchInfo.icon"
                      :class="getMatchColorClass(zipMatchInfo.color)"
                      class="w-4 h-4 shrink-0"
                    />
                    <UBadge
                      :color="zipMatchInfo.color"
                      variant="subtle"
                      size="xs"
                      class="font-mono font-bold"
                    >
                      Code {{ zipMatchInfo.code }}
                    </UBadge>
                    <span class="font-medium text-gray-800 dark:text-gray-200">
                      {{ zipMatchInfo.meaning }}
                    </span>
                  </template>
                  <span v-else class="text-gray-400 italic">Nicht geprüft</span>
                </div>
              </div>

              <!-- Ort -->
              <div
                class="grid grid-cols-12 px-3 py-2.5 items-center hover:bg-gray-50/50 dark:hover:bg-gray-800/30 text-xs"
              >
                <div class="col-span-3 font-medium text-gray-600 dark:text-gray-400">Ort</div>
                <div
                  class="col-span-4 text-gray-900 dark:text-white truncate"
                  :title="protocol.submittedData?.city"
                >
                  {{ protocol.submittedData?.city || protocol.rawResponse?.ort || '-' }}
                </div>
                <div class="col-span-5 flex items-center gap-1.5 flex-wrap">
                  <template v-if="cityMatchInfo.code !== '-'">
                    <UIcon
                      :name="cityMatchInfo.icon"
                      :class="getMatchColorClass(cityMatchInfo.color)"
                      class="w-4 h-4 shrink-0"
                    />
                    <UBadge
                      :color="cityMatchInfo.color"
                      variant="subtle"
                      size="xs"
                      class="font-mono font-bold"
                    >
                      Code {{ cityMatchInfo.code }}
                    </UBadge>
                    <span class="font-medium text-gray-800 dark:text-gray-200">
                      {{ cityMatchInfo.meaning }}
                    </span>
                  </template>
                  <span v-else class="text-gray-400 italic">Nicht geprüft</span>
                </div>
              </div>

              <!-- Eigene USt-IdNr. & Land -->
              <div
                class="grid grid-cols-12 px-3 py-2 bg-gray-50/50 dark:bg-gray-800/20 text-xs text-gray-500"
              >
                <div class="col-span-3 font-medium">Anfragende DE USt-IdNr.</div>
                <div class="col-span-4 font-mono">
                  {{
                    protocol.submittedData?.ownVatId || protocol.rawResponse?.anfragendeUstid || '-'
                  }}
                </div>
                <div class="col-span-5">
                  Land:
                  <span class="font-medium text-gray-700 dark:text-gray-300">{{
                    protocol.submittedData?.country || '-'
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 4. Raw Response / Protokoll JSON -->
          <div>
            <div class="flex items-center justify-between mb-2">
              <div class="flex items-center gap-2">
                <UIcon name="i-lucide-code-2" class="w-4 h-4 text-primary" />
                <h4
                  class="font-semibold text-xs uppercase tracking-wider text-gray-700 dark:text-gray-300"
                >
                  {{ isGermanFormal ? 'Prüfprotokoll (JSON)' : 'Raw BZSt Response / Protokoll (JSON)' }}
                </h4>
              </div>
              <span v-if="isGermanFormal" class="text-[11px] text-gray-400">
                Systemprotokoll (keine BZSt-Antwort)
              </span>
            </div>
            <pre
              class="p-3.5 bg-gray-900 text-gray-100 rounded-lg text-xs overflow-x-auto font-mono max-h-56 border border-gray-800"
              >{{ JSON.stringify(isGermanFormal ? protocol : (protocol.rawResponse || protocol), null, 2) }}</pre>
          </div>

          <!-- 5. Bedeutung der RAW Response (Nur bei ausländischen EU-Prüfungen mit RAW Response) -->
          <div v-if="!isGermanFormal && rawFieldsBreakdown.length > 0" class="pt-1">
            <div
              class="rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50/70 dark:bg-gray-800/40 p-3.5 space-y-2.5"
            >
              <div class="flex items-center gap-2">
                <UIcon name="i-lucide-file-text" class="w-4 h-4 text-primary" />
                <h4
                  class="font-semibold text-xs uppercase tracking-wider text-gray-700 dark:text-gray-300"
                >
                  Bedeutung der Werte in dieser RAW Response
                </h4>
              </div>
              <div class="grid grid-cols-1 gap-2 text-xs">
                <div
                  v-for="item in rawFieldsBreakdown"
                  :key="item.key"
                  class="flex items-start gap-2.5 p-2.5 bg-white dark:bg-gray-900 rounded-md border border-gray-100 dark:border-gray-800"
                >
                  <span
                    class="font-mono text-xs font-semibold text-gray-500 dark:text-gray-400 min-w-[130px] shrink-0"
                  >
                    {{ item.key }}:
                  </span>
                  <div class="flex items-center gap-2 flex-wrap">
                    <UBadge
                      :color="item.color"
                      size="xs"
                      variant="subtle"
                      class="font-mono font-bold"
                    >
                      {{ item.code ? `Code ${item.code}` : item.value }}
                    </UBadge>
                    <span class="font-semibold text-gray-900 dark:text-white">
                      {{ item.label }}
                    </span>
                    <span class="text-gray-600 dark:text-gray-400"> — {{ item.meaning }} </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </UCard>
    </template>
  </UModal>
</template>

<script lang="ts" setup>
import { computed, type PropType } from 'vue'
import type { AuditProtocol } from '@/api/vatValidation'
import { getMatchCodeInfo, getBzstStatusDescription } from '@/utils/vatCodes'

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  protocol: {
    type: Object as PropType<AuditProtocol | null>,
    default: null,
  },
  title: {
    type: String,
    default: 'Prüfprotokoll Details',
  },
})

const emit = defineEmits(['update:open'])

const isOpen = computed({
  get: () => props.open,
  set: (val: boolean) => emit('update:open', val),
})

const isAllA = computed(() => {
  return (
    companyMatch.value === 'A' &&
    streetMatch.value === 'A' &&
    zipMatch.value === 'A' &&
    cityMatch.value === 'A'
  )
})

const effectiveStatusDisplay = computed(() => {
  if (!props.protocol) return ''
  const hasMatches = Boolean(
    companyMatch.value || streetMatch.value || zipMatch.value || cityMatch.value,
  )

  if (props.protocol.status === 'OFFICIALLY_CONFIRMED') {
    if (hasMatches && !isAllA.value) {
      return 'Ausland: Eingeschränkt gültig'
    }
    return props.protocol.statusDisplay || 'Ausland: Gültig'
  }
  if (props.protocol.status === 'PARTIALLY_CONFIRMED') {
    return 'Ausland: Eingeschränkt gültig'
  }
  if (props.protocol.status === 'OFFICIALLY_NOT_CONFIRMED') {
    return 'Ausland: Ungültig'
  }
  if (
    props.protocol.status === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' ||
    props.protocol.status === 'FORMALLY_VALID'
  ) {
    return 'Inland: Gültig'
  }
  if (props.protocol.status === 'FORMALLY_INVALID' || props.protocol.status === 'FORMAT_ERROR') {
    if (
      !props.protocol.submittedData?.requestedVatId &&
      !props.protocol.rawResponse?.angefragteUstid
    ) {
      return 'Keine USt-IdNr.'
    }
    return 'Inland: Ungültig'
  }
  if (props.protocol.status === 'MISSING_VAT_ID') {
    return 'Keine USt-IdNr.'
  }
  return props.protocol.statusDisplay || props.protocol.status
})

const statusBadgeColor = computed(() => {
  const status = props.protocol?.status
  if (status === 'OFFICIALLY_CONFIRMED') {
    const hasMatches = Boolean(
      companyMatch.value || streetMatch.value || zipMatch.value || cityMatch.value,
    )
    if (hasMatches && !isAllA.value) {
      return 'warning'
    }
    return 'success'
  }
  switch (status) {
    case 'PARTIALLY_CONFIRMED':
      return 'warning'
    case 'FORMALLY_VALID_NO_OFFICIAL_CHECK':
      return 'info'
    case 'CHECK_NOT_POSSIBLE':
      return 'warning'
    case 'OFFICIALLY_NOT_CONFIRMED':
    case 'FORMALLY_INVALID':
    case 'TECHNICAL_ERROR':
      return 'error'
    default:
      return 'neutral'
  }
})

const isGermanFormal = computed(() => {
  if (
    props.protocol?.validationType === 'FORMAL' ||
    props.protocol?.interface === 'REGEX_DE_PATTERN'
  ) {
    return true
  }
  const s = props.protocol?.status
  if (s === 'FORMALLY_VALID_NO_OFFICIAL_CHECK' || s === 'FORMALLY_INVALID') {
    return true
  }
  const vat = String(
    props.protocol?.submittedData?.requestedVatId ||
      props.protocol?.rawResponse?.angefragteUstid ||
      '',
  )
  if (vat && vat.toUpperCase().startsWith('DE')) {
    return true
  }
  const country = props.protocol?.submittedData?.country
  if (
    country &&
    ['DE', 'DEU', 'DEUTSCHLAND', 'GERMANY'].includes(country.toUpperCase())
  ) {
    return true
  }
  return false
})

const officialStatusCode = computed(() => {
  return String(
    props.protocol?.result?.officialStatusCode || props.protocol?.rawResponse?.status || '',
  )
})

const officialStatusMessage = computed(() => {
  if (props.protocol?.result?.officialMessage) {
    return props.protocol.result.officialMessage
  }
  if (officialStatusCode.value) {
    return getBzstStatusDescription(officialStatusCode.value)
  }
  return isGermanFormal.value
    ? 'Formelle Syntaxprüfung durchgeführt.'
    : 'Amtliche Prüfung durchgeführt.'
})

const companyMatch = computed(() =>
  props.protocol?.result?.companyNameMatch || props.protocol?.rawResponse?.ergFirmenname
    ? String(props.protocol?.result?.companyNameMatch || props.protocol?.rawResponse?.ergFirmenname)
    : '',
)
const streetMatch = computed(() =>
  props.protocol?.result?.streetMatch || props.protocol?.rawResponse?.ergStrasse
    ? String(props.protocol?.result?.streetMatch || props.protocol?.rawResponse?.ergStrasse)
    : '',
)
const zipMatch = computed(() =>
  props.protocol?.result?.zipMatch || props.protocol?.rawResponse?.ergPlz
    ? String(props.protocol?.result?.zipMatch || props.protocol?.rawResponse?.ergPlz)
    : '',
)
const cityMatch = computed(() =>
  props.protocol?.result?.cityMatch || props.protocol?.rawResponse?.ergOrt
    ? String(props.protocol?.result?.cityMatch || props.protocol?.rawResponse?.ergOrt)
    : '',
)

const companyMatchInfo = computed(() => getMatchCodeInfo(companyMatch.value))
const streetMatchInfo = computed(() => getMatchCodeInfo(streetMatch.value))
const zipMatchInfo = computed(() => getMatchCodeInfo(zipMatch.value))
const cityMatchInfo = computed(() => getMatchCodeInfo(cityMatch.value))

interface RawFieldItem {
  key: string
  label: string
  value: string
  code?: string
  meaning: string
  color: 'success' | 'error' | 'neutral' | 'warning' | 'primary'
}

const rawFieldsBreakdown = computed<RawFieldItem[]>(() => {
  const raw = props.protocol?.rawResponse
  if (!raw) return []

  const items: RawFieldItem[] = []

  if (raw.id) {
    items.push({
      key: 'id',
      label: 'Amtliche BZSt Request-ID',
      value: String(raw.id),
      meaning: 'Eindeutige amtliche Nachweisnummer des BZSt (§ 17c UStDV)',
      color: 'primary',
    })
  }

  if (raw.status) {
    const statusDesc = getBzstStatusDescription(String(raw.status))
    items.push({
      key: 'status',
      label: 'Status-Code',
      value: String(raw.status),
      meaning: statusDesc || 'Status der Bestätigungsanfrage',
      color: raw.status === 'evatr-0000' ? 'success' : 'error',
    })
  }

  if (raw.ergFirmenname !== undefined) {
    const info = getMatchCodeInfo(raw.ergFirmenname != null ? String(raw.ergFirmenname) : undefined)
    items.push({
      key: 'ergFirmenname',
      label: 'Firmenname',
      value: String(raw.ergFirmenname),
      code: info.code,
      meaning: `${info.meaning} (${info.description})`,
      color: info.color,
    })
  }

  if (raw.ergStrasse !== undefined) {
    const info = getMatchCodeInfo(raw.ergStrasse != null ? String(raw.ergStrasse) : undefined)
    items.push({
      key: 'ergStrasse',
      label: 'Straße',
      value: String(raw.ergStrasse),
      code: info.code,
      meaning: `${info.meaning} (${info.description})`,
      color: info.color,
    })
  }

  if (raw.ergPlz !== undefined) {
    const info = getMatchCodeInfo(raw.ergPlz != null ? String(raw.ergPlz) : undefined)
    items.push({
      key: 'ergPlz',
      label: 'Postleitzahl',
      value: String(raw.ergPlz),
      code: info.code,
      meaning: `${info.meaning} (${info.description})`,
      color: info.color,
    })
  }

  if (raw.ergOrt !== undefined) {
    const info = getMatchCodeInfo(raw.ergOrt != null ? String(raw.ergOrt) : undefined)
    items.push({
      key: 'ergOrt',
      label: 'Ort',
      value: String(raw.ergOrt),
      code: info.code,
      meaning: `${info.meaning} (${info.description})`,
      color: info.color,
    })
  }

  if (raw.anfrageZeitpunkt) {
    items.push({
      key: 'anfrageZeitpunkt',
      label: 'Anfragezeitpunkt',
      value: String(raw.anfrageZeitpunkt),
      meaning: `Amtlicher Zeitstempel der BZSt-Server (${formatTimestamp(String(raw.anfrageZeitpunkt))})`,
      color: 'neutral',
    })
  }

  if (raw.gueltigAb) {
    items.push({
      key: 'gueltigAb',
      label: 'Gültig ab',
      value: String(raw.gueltigAb),
      meaning: 'Beginn der Gültigkeit der angefragten USt-IdNr.',
      color: 'neutral',
    })
  }

  if (raw.gueltigBis) {
    items.push({
      key: 'gueltigBis',
      label: 'Gültig bis',
      value: String(raw.gueltigBis),
      meaning: 'Ende der Gültigkeit der angefragten USt-IdNr.',
      color: 'neutral',
    })
  }

  return items
})

function getMatchColorClass(color: string) {
  switch (color) {
    case 'success':
      return 'text-green-500 dark:text-green-400'
    case 'error':
      return 'text-red-500 dark:text-red-400'
    case 'warning':
      return 'text-amber-500 dark:text-amber-400'
    default:
      return 'text-gray-400 dark:text-gray-500'
  }
}

function formatTimestamp(ts?: string) {
  if (!ts) return '-'
  try {
    return new Date(ts).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return ts
  }
}
</script>
